/**
 * Reviews Section Component for Customer Testimonial Display
 * 
 * A horizontal scrolling testimonial section that showcases authentic
 * customer reviews and experiences with WoodAlps bikes. Features
 * intelligent data processing and responsive carousel presentation.
 * 
 * Features:
 * - Horizontal scrolling carousel for review cards
 * - Intelligent customer name and bike data processing
 * - Hidden scrollbar for clean visual presentation
 * - Responsive design optimized for mobile and desktop
 * - Gradient background for visual appeal
 * 
 * Data Processing:
 * - Automatic customer name splitting (first/last name)
 * - Bike model and size parsing from combined strings
 * - Customer image integration for review cards
 * - Rating system integration (1-5 stars)
 * - Flexible review text handling
 * 
 * Visual Design:
 * - Gradient background from wood-brown to white
 * - Horizontal carousel with gap spacing
 * - Hidden scrollbars for clean appearance
 * - Brand font integration (Neuropol for headings)
 * - Professional spacing and layout
 * 
 * Content Structure:
 * - Customer profile information (name, country)
 * - Product information (bike model, size)
 * - Review content (rating, text, images)
 * - Responsive card layout with consistent sizing
 * 
 * User Experience:
 * - Smooth horizontal scrolling for review browsing
 * - Touch-friendly mobile carousel interaction
 * - Clear visual hierarchy for review content
 * - Easy scanning of customer feedback
 * 
 * Performance:
 * - Efficient data processing with mapping
 * - Optimized rendering with stable keys
 * - CSS-in-JS for scrollbar styling
 * - Component composition for reusability
 * 
 * Accessibility:
 * - Semantic HTML structure for testimonials
 * - Keyboard navigation for carousel scrolling
 * - Screen reader compatible review content
 * - Clear content organization and hierarchy
 * 
 * @param {string} title - Section heading (default: 'Customer Reviews')
 * @param {Array} reviews - Array of review objects from Sanity CMS
 * @param {string} reviews[].customerName - Full customer name
 * @param {string} reviews[].country - Customer location
 * @param {string} reviews[].bikeType - Combined bike model and size
 * @param {number} reviews[].rating - Star rating (1-5)
 * @param {string} reviews[].reviewText - Review content
 * @param {Array} reviews[].customerImages - Customer photos (max 3)
 * 
 * @description Horizontal scrolling customer testimonial section with intelligent data processing
 */

import React, { useState, useRef, useEffect } from 'react';
import ReviewCard from '../products/ReviewCard';

const ReviewsSection = ({ 
  title = 'Customer Reviews',
  reviews = []
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentTransform, setCurrentTransform] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [showOriginalOnly, setShowOriginalOnly] = useState(false);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Constants for card dimensions
  const CARD_WIDTH = 350; // Width of each review card in pixels
  const CARD_GAP = 24;    // Gap between cards in pixels
  const CARD_TOTAL_WIDTH = CARD_WIDTH + CARD_GAP; // Total width per card including gap

  // Process reviews to match ReviewCard props format
  const processedReviews = reviews.map(review => {
    // Split customerName into firstName and lastName
    const nameParts = (review.customerName || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Split bikeType into model and size if possible
    const bikeTypeParts = (review.bikeType || '').split(' ');
    const bikeSize = bikeTypeParts.length > 1 ? bikeTypeParts.pop() : '';
    const bikeModel = bikeTypeParts.join(' ');
    
    // Process customerImages array (now supports up to 3 images)
    const images = review.customerImages || [];
    
    return {
      firstName,
      lastName,
      country: review.country || '',
      bikeModel,
      bikeSize,
      rating: review.rating || 5,
      reviewText: review.reviewText || '',
      images
    };
  });

  // Determine if animation should be active (only if 4+ reviews)
  const shouldAnimate = processedReviews.length >= 4;
  
  // Display reviews: original only when paused, duplicated when animating
  const displayedReviews = showOriginalOnly 
    ? processedReviews
    : (shouldAnimate ? [...processedReviews, ...processedReviews] : processedReviews);

  /**
   * Find the first card that is 100% visible in the viewport
   * @param {number} currentTranslateX - Current transform translateX value
   * @returns {number} - Index of the first fully visible card in the original reviews array
   */
  const findFirstFullyVisibleCard = (currentTranslateX) => {
    if (!containerRef.current) return 0;

    const containerRect = containerRef.current.getBoundingClientRect();
    const viewportLeft = containerRect.left;

    // Calculate which card is first fully visible based on transform
    const absoluteOffset = Math.abs(currentTranslateX);
    let cardIndex = Math.floor(absoluteOffset / CARD_TOTAL_WIDTH);

    // Check if this card is actually 100% visible (not cut off on the left)
    const cardLeft = viewportLeft - currentTranslateX + (cardIndex * CARD_TOTAL_WIDTH);

    // If card is partially hidden on the left, move to next card
    if (cardLeft < viewportLeft) {
      cardIndex++;
    }

    // Normalize to original reviews array (handle duplicates)
    // If we're in the second half of duplicated array, map back to original
    return cardIndex % processedReviews.length;
  };

  /**
   * Calculate snap position to center a clicked card with context
   * Centers the card in the viewport with surrounding cards visible when possible
   * @param {number} cardIndex - Index of the clicked card in original reviews array
   * @returns {number} - Transform translateX value for centered alignment
   */
  const calculateCenteredSnapPosition = (cardIndex) => {
    if (!containerRef.current) return 0;

    // Get viewport width to calculate how many cards fit
    const viewportWidth = containerRef.current.offsetWidth;
    
    // Calculate how many full cards can fit in the viewport
    const cardsInView = Math.floor(viewportWidth / CARD_TOTAL_WIDTH);
    
    // Calculate offset to center the clicked card
    // Example: If 3 cards fit, and we click card at index 2, 
    // we want to show [card1, card2, card3] with card2 centered
    const centerOffset = Math.floor(cardsInView / 2);
    
    // Calculate the target position: move left by (cardIndex - centerOffset) cards
    let targetOffset = cardIndex - centerOffset;
    
    // Ensure we don't scroll before the first card
    targetOffset = Math.max(0, targetOffset);
    
    // Ensure we don't scroll past the last card
    // Maximum offset is (totalCards - cardsInView) to keep last card visible
    const maxOffset = Math.max(0, processedReviews.length - cardsInView);
    targetOffset = Math.min(targetOffset, maxOffset);
    
    // Convert card offset to pixel position
    return -(targetOffset * CARD_TOTAL_WIDTH);
  };

  /**
   * Handle pause: Stop animation, reset to beginning, show originals only
   * Always snaps back to the start [A, B, C, D] for consistent user experience
   */
  const handlePause = () => {
    if (!shouldAnimate || isPaused) return;

    // Always snap to position 0 (beginning of reviews)
    // This shows [A, B, C, D] from the start regardless of where animation was
    setCurrentTransform(0);
    setShowOriginalOnly(true);
    setIsPaused(true);

    // Reset scroll position reference
    scrollPositionRef.current = 0;
  };

  /**
   * Handle image click: Pause animation and disable body scroll
   */
  const handleImageClick = () => {
    handlePause();
    setModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  /**
   * Handle resume: Restore duplicated reviews, resume animation from manually scrolled position
   * Called when user closes an image modal, seamlessly resuming infinite scroll animation
   */
  const handleResume = () => {
    if (!shouldAnimate) return;

    setModalOpen(false);
    document.body.style.overflow = ''; // Restore body scroll
    
    // Small delay to ensure modal closes smoothly before resuming animation
    setTimeout(() => {
      if (containerRef.current) {
        // Get the current manual scroll position (user may have scrolled while paused)
        const currentScroll = containerRef.current.scrollLeft;
        
        // Calculate which card index we're currently viewing
        // This determines where to resume the animation from
        const cardIndex = Math.floor(currentScroll / CARD_TOTAL_WIDTH);
        
        // Calculate the transform position for this card
        const newTransform = -(cardIndex * CARD_TOTAL_WIDTH);
        
        // Restore duplicated reviews for infinite loop
        setShowOriginalOnly(false);
        
        // Set the starting transform position for animation resume
        setCurrentTransform(newTransform);
        
        // Resume the infinite scroll animation
        setIsPaused(false);
      }
    }, 50);
  };
  
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-wood-brown/10 to-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center font-neuropol">
          {title}
        </h2>
        
        <div 
          ref={containerRef}
          className={`relative ${isPaused || !shouldAnimate ? 'overflow-x-auto' : 'overflow-hidden'} pb-8`}
        >
          {/* Infinite Scrolling Reviews Carousel */}
          <div 
            ref={carouselRef}
            className={`flex gap-6 ${!shouldAnimate || isPaused ? '' : 'animate-scroll-reviews'}`}
            style={{ 
              width: 'fit-content',
              transform: isPaused && shouldAnimate ? `translateX(${currentTransform}px)` : undefined,
              transition: isPaused ? 'none' : undefined // No transition when paused for instant snap
            }}
          >
            {displayedReviews.map((review, index) => (
              <div 
                key={`review-${index}`}
                className="flex-shrink-0 w-[350px]"
                onClick={handlePause}
              >
                <ReviewCard
                  firstName={review.firstName}
                  lastName={review.lastName}
                  country={review.country}
                  bikeModel={review.bikeModel}
                  bikeSize={review.bikeSize}
                  rating={review.rating}
                  reviewText={review.reviewText}
                  images={review.images}
                  onImageClick={handleImageClick}
                  onImageClose={handleResume}
                  isModalIndependent={true}
                />
              </div>
            ))}
          </div>
          
          {/* Animation and styling */}
          <style jsx>{`
            @keyframes scrollReviews {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(calc(-100% / 2));
              }
            }

            .animate-scroll-reviews {
              animation: scrollReviews 40s linear infinite;
              will-change: transform;
            }

            /* Custom scrollbar for manual scrolling in paused state */
            div::-webkit-scrollbar {
              height: 8px;
            }

            div::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 4px;
            }

            div::-webkit-scrollbar-thumb {
              background: #888;
              border-radius: 4px;
            }

            div::-webkit-scrollbar-thumb:hover {
              background: #555;
            }
          `}</style>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection; 